// src/hooks/useAutoUpdate.ts
//
// Auto-reload the WebView when a newer build has been deployed.
//
// Why this exists: the Capacitor APK uses `server.url = https://spent-tracker.pages.dev`,
// so the WebView caches `index.html` (and its hashed chunk imports) for the
// lifetime of the process. Backgrounding via the home button does NOT discard
// the WebView — the user has to fully kill the app from Recents to pick up a
// new deploy. Annoying for a daily-use app the developer keeps shipping.
//
// Strategy: on every resume (Capacitor `appStateChange` → isActive=true,
// plus browser `visibilitychange` for the web/PWA path), fetch the static
// `/version.json` Pages-emitted manifest with `cache: 'no-store'`. Compare its
// `version` against the compile-time-baked `__APP_VERSION__`. If they differ,
// reload the WebView.
//
// Safety: if a form input has focus we don't yank the page out from under
// the user — we toast instead so they can finish + manually reload.

import { useEffect, useRef } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast';

declare const __APP_VERSION__: string;

const VERSION_URL = '/version.json';
// Don't hammer the CDN if the user task-switches back-and-forth rapidly.
const MIN_CHECK_GAP_MS = 20_000;

function isUserMidEdit(): boolean {
  const active = document.activeElement;
  if (!active) return false;
  if (!(active instanceof HTMLElement)) return false;
  const tag = active.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    // contenteditable / readonly inputs don't count
    const ro = (active as HTMLInputElement).readOnly;
    return !ro;
  }
  if (active.isContentEditable) return true;
  return false;
}

export function useAutoUpdate() {
  const { t } = useTranslation();
  const lastCheckRef = useRef(0);
  const promptedVersionRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const now = Date.now();
      if (now - lastCheckRef.current < MIN_CHECK_GAP_MS) return;
      lastCheckRef.current = now;

      try {
        // Cache-bust the URL on top of cache:'no-store' — defensive against
        // aggressive Android WebView caches in some OEM ROMs.
        const res = await fetch(`${VERSION_URL}?t=${now}`, { cache: 'no-store' });
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as { version?: unknown };
        const latest = typeof data.version === 'string' ? data.version : null;
        if (!latest || latest === __APP_VERSION__) return;

        // Already showed the prompt for this exact version this session?
        // Don't re-toast every 20s.
        if (promptedVersionRef.current === latest) return;
        promptedVersionRef.current = latest;

        console.info('[auto-update] new build available', {
          running: __APP_VERSION__,
          latest,
        });

        if (isUserMidEdit()) {
          // Don't disrupt — let them finish, give them a manual hint.
          toast.info(t('autoUpdate.availableManual', { version: latest }));
          return;
        }

        toast.success(t('autoUpdate.reloading', { version: latest }));
        // Tiny delay so the toast actually renders before the WebView
        // unloads. 600ms is plenty on Android Chrome.
        window.setTimeout(() => {
          window.location.reload();
        }, 600);
      } catch {
        // Network blip / offline — silently retry on next resume.
      }
    };

    // Web / PWA path — fires on tab become-visible.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);

    // Capacitor path — fires when the OS brings the app back to foreground.
    let nativeSub: { remove: () => void } | null = null;
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void check();
    })
      .then((s) => {
        if (cancelled) {
          s.remove();
        } else {
          nativeSub = s;
        }
      })
      .catch(() => {
        // Capacitor not available (pure web) — that's fine, visibilitychange
        // handles it.
      });

    // Initial mount check — useful only if the WebView was kept alive across
    // a deploy without the OS firing appStateChange (rare but possible).
    void check();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      nativeSub?.remove();
    };
  }, [t]);
}
